import BaseExportStrategy from './BaseExportStrategy';

/**
 * Estrategia de exportación para reportes de Financiamiento
 * Define las columnas y el formato específico para Financiamiento
 */
export default class FinanciamientoExportStrategy extends BaseExportStrategy {
  constructor() {
    super('Financiamiento');
  }

  getTitle(context) {
    return 'INFORME DE FINANCIAMIENTO';
  }

  /**
   * Define las columnas del reporte de Financiamiento
   */
  getColumns(options = {}) {
    const columns = [];

    // 1. Legajo
    columns.push({
      key: 'legajo',
      header: 'Legajo',
      pdfWidth: 15,
      excelWidth: 12,
      align: 'left',

      format: (item, ctx) => item.id || '-',

      formatSubtotal: (item, ctx) => 'SUBTOTAL',

      formatTotal: (items, ctx) => ''  // Vacío - el texto va en Fideicomiso
    });

    // 2. Fideicomiso
    columns.push({
      key: 'fideicomiso',
      header: 'Fideicomiso',
      pdfWidth: 30,
      excelWidth: 30,
      align: 'left',

      format: (item, ctx) => item.fideicomisoName || '-',

      formatSubtotal: (item, ctx) => `${item._cantidadRegistros || 0} registros`,

      formatTotal: (items, ctx) => 'TOTAL - RESUMEN'  // Texto aquí
    });

    // 3. Operatoria / Línea
    columns.push({
      key: 'operatoria',
      header: 'Operatoria / Línea',
      pdfWidth: 38,
      excelWidth: 35,
      align: 'left',

      format: (item, ctx) => item.operatoria || '-',

      formatSubtotal: (item, ctx) => {
        return (item._subtotales && item._subtotales.operatoria) || item.operatoria || '-';
      },

      formatTotal: (items, ctx) => ''
    });

    // 4. Apellido y Nombre / Razón Social
    columns.push({
      key: 'cliente',
      header: 'Apellido y Nombre / Razón Social',
      pdfWidth: 40,
      excelWidth: 40,
      align: 'left',

      format: (item, ctx) => {
        if (!item.cliente) return '-';
        return item.cliente.razonSocial.toUpperCase() ||
               (item.cliente.apellido + ' ' + item.cliente.nombre).trim().toUpperCase() ||
               '-';
      },

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 5. CUIT
    columns.push({
      key: 'cuit',
      header: 'CUIT',
      pdfWidth: 22,
      excelWidth: 15,
      align: 'left',

      format: (item, ctx) => (item.cliente && item.cliente.cuit) || '-',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 6. Departamento
    columns.push({
      key: 'departamento',
      header: 'Departamento',
      pdfWidth: 25,
      excelWidth: 20,
      align: 'left',

      format: (item, ctx) => {
        // ✅ PRIMERO: Verificar si el departamento ya viene del backend
        if (item.departamento && item.departamento !== '') {
          return item.departamento;
        }
        // Intentar obtener departamento desde diferentes estructuras
        if (item.localidad && item.localidad.departamento && item.localidad.departamento.nombre) {
          return item.localidad.departamento.nombre;
        }
        if (item.cliente && item.cliente.localidad && item.cliente.localidad.departamento && item.cliente.localidad.departamento.nombre) {
          return item.cliente.localidad.departamento.nombre;
        }
        // Fallback a localidad si no hay departamento (compatibilidad)
        if (item.cliente && item.cliente.localidad) {
          return item.cliente.localidad;
        }
        if (item.localidad && typeof item.localidad === 'string') {
          return item.localidad;
        }
        if (item.localidad && item.localidad.nombre) {
          return item.localidad.nombre;
        }
        return '-';
      },

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 7. Persona
    columns.push({
      key: 'persona',
      header: 'Persona',
      pdfWidth: 18,
      excelWidth: 12,
      align: 'left',

      format: (item, ctx) => {
        if (!item.cliente) return '-';
        return item.cliente.tipoCliente === 0 ? 'Jurídica' : 'Física';
      },

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 8. Fecha de Ingreso
    columns.push({
      key: 'fechaIngreso',
      header: 'Fecha de Ingreso',
      pdfWidth: 22,
      excelWidth: 15,
      align: 'left',

      format: (item, ctx) => this.formatDates(item.fechaIngreso),

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 9. Estado
    columns.push({
      key: 'estado',
      header: 'Estado',
      pdfWidth: 18,
      excelWidth: 12,
      align: 'left',

      format: (item, ctx) => item.estado || '-',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 10. Monto Aprobado
    columns.push({
      key: 'monto_aprobado',
      header: 'Monto Aprobado',
      pdfWidth: 24,
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        const value = this.getTipoMonto(item.parametros, 'monto_aprobado') || item.monto_aprobado || 0;
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx) => {
        const value = (item._subtotales && item._subtotales.montoAprobado) || 0;
        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const monto = this.getTipoMonto(item.parametros, 'monto_aprobado') || item.monto_aprobado || 0;
          return total + parseFloat(monto);
        }, 0);
      }
    });

    // 11. Monto Otorgado
    columns.push({
      key: 'monto',
      header: 'Monto Otorgado',
      pdfWidth: 24,
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        // Solo mostrar monto si el estado es "Otorgado" o "Cancelado"
        if (!this.esEstadoValidoParaMonto(item)) {
          return this.formatMonto(0);
        }
        const value = this.getTipoMonto(item.parametros, 'monto');
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx, allData) => {
        // NO usar el valor del backend porque no calcula correctamente
        // Recalcular sumando los items de la operatoria con estado válido
        const operatoriaNombre = (item._subtotales && item._subtotales.operatoria) || item.operatoria.replace('Subtotal ', '');

        // Filtrar solo items regulares (no subtotales) de la misma operatoria
        const itemsDeOperatoria = allData.filter(dataItem =>
          !dataItem._esSubtotal &&
          dataItem.operatoria === operatoriaNombre
        );

        // Sumar solo items con estado válido
        const value = itemsDeOperatoria.reduce((total, dataItem) => {
          if (!this.esEstadoValidoParaMonto(dataItem)) {
            return total;
          }
          const monto = this.getTipoMonto(dataItem.parametros, 'monto');
          return total + parseFloat(monto || 0);
        }, 0);

        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          // Solo sumar si el estado es válido
          if (!this.esEstadoValidoParaMonto(item)) {
            return total;
          }
          const monto = this.getTipoMonto(item.parametros, 'monto');
          return total + parseFloat(monto);
        }, 0);
      }
    });

    // 12. Monto Pendiente
    columns.push({
      key: 'monto_pendiente',
      header: 'Monto Pendiente',
      pdfWidth: 24,
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        const value = this.getPendienteDesembolso(item);
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx) => {
        const value = (item._subtotales && item._subtotales.montoPendiente) || 0;
        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const montoPendiente = this.getPendienteDesembolso(item);
          return total + parseFloat(montoPendiente);
        }, 0);
      }
    });

    // 13. Fecha de Otorgamiento
    columns.push({
      key: 'fechaOtorgamiento',
      header: 'Fecha de Otorgamiento',
      pdfWidth: 22,
      excelWidth: 18,
      align: 'left',

      format: (item, ctx) => item.fechaOtorgamiento || '-',

      formatSubtotal: (item, ctx) => '',

      formatTotal: (items, ctx) => ''
    });

    // 14. Honorarios MF
    columns.push({
      key: 'honorarios_mf',
      header: 'Honorarios MF',
      pdfWidth: 22,
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        const value = (item.gastos && item.gastos.honorarios_mf) ? parseFloat(item.gastos.honorarios_mf) : 0;
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx) => {
        const value = this.calcularSubtotalGasto(item, 'honorarios_mf', items => items);
        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const honorariosMf = (item.gastos && item.gastos.honorarios_mf) ? parseFloat(item.gastos.honorarios_mf) : 0;
          return total + honorariosMf;
        }, 0);
      }
    });

    // 15. Gastos de Escribano
    columns.push({
      key: 'gastos_escribano',
      header: 'Gastos de Escribano',
      pdfWidth: 22,
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        const value = (item.gastos && item.gastos.gastos_escribano) ? parseFloat(item.gastos.gastos_escribano) : 0;
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx) => {
        const value = this.calcularSubtotalGasto(item, 'gastos_escribano', items => items);
        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const gastosEscribano = (item.gastos && item.gastos.gastos_escribano) ? parseFloat(item.gastos.gastos_escribano) : 0;
          return total + gastosEscribano;
        }, 0);
      }
    });

    // 16. Otros Gastos
    columns.push({
      key: 'otros_gastos',
      header: 'Otros Gastos',
      pdfWidth: 22,
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        const value = (item.gastos && item.gastos.otros_gastos) ? parseFloat(item.gastos.otros_gastos) : 0;
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx) => {
        const value = this.calcularSubtotalGasto(item, 'otros_gastos', items => items);
        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const otrosGastos = (item.gastos && item.gastos.otros_gastos) ? parseFloat(item.gastos.otros_gastos) : 0;
          return total + otrosGastos;
        }, 0);
      }
    });

    // 17-N. Desembolsos dinámicos
    const maxDesembolsos = this.getMaxDesembolsos(options);
    for (let i = 1; i <= maxDesembolsos; i++) {
      // Monto del desembolso
      columns.push({
        key: `desembolso_${i}`,
        header: `Desembolso ${i}`,
        pdfWidth: 22,
        excelWidth: 16,
        align: 'right',

        format: (item, ctx) => {
          // Solo mostrar desembolsos si el estado es válido
          if (!this.esEstadoValidoParaMonto(item)) {
            return '-';
          }
          const desembolso = this.getDesembolso(item, i);
          if (desembolso) {
            return this.formatMonto(desembolso.monto);
          }
          // Para Financiamiento, si es el primer desembolso y no hay desembolsos, usar monto de parametros
          if (i === 1 && (!item.desembolsos || item.desembolsos.length === 0)) {
            const value = this.getTipoMonto(item.parametros, 'monto');
            return this.formatMonto(value);
          }
          return '-';
        },

        formatSubtotal: (item, ctx) => {
          const value = (item._subtotales && item._subtotales.desembolsos && item._subtotales.desembolsos[i]) || 0;
          return this.formatMonto(value);
        },

        calculateTotal: (items, ctx) => {
          return items.reduce((total, item) => {
            // Solo sumar si el estado es válido
            if (!this.esEstadoValidoParaMonto(item)) {
              return total;
            }
            const desembolso = this.getDesembolso(item, i);
            if (desembolso) {
              return total + parseFloat(desembolso.monto || 0);
            }
            // Para Financiamiento, si es el primer desembolso y no hay desembolsos, usar monto de parametros
            if (i === 1 && (!item.desembolsos || item.desembolsos.length === 0)) {
              const value = this.getTipoMonto(item.parametros, 'monto');
              return total + parseFloat(value);
            }
            return total;
          }, 0);
        }
      });

      // Fecha del desembolso
      columns.push({
        key: `fecha_desembolso_${i}`,
        header: `Fecha Desembolso ${i}`,
        pdfWidth: 22,
        excelWidth: 16,
        align: 'left',

        format: (item, ctx) => {
          const desembolso = this.getDesembolso(item, i);
          if (desembolso && desembolso.fecha_desembolso) {
            return desembolso.fecha_desembolso;
          }
          // Para Financiamiento, si es el primer desembolso y no hay desembolsos, usar fechaOtorgamiento
          if (i === 1 && (!item.desembolsos || item.desembolsos.length === 0)) {
            return item.fechaOtorgamiento || '-';
          }
          return '-';
        },

        formatSubtotal: (item, ctx) => '',

        formatTotal: (items, ctx) => ''
      });
    }

    // Última columna: Monto Liquidado
    columns.push({
      key: 'liquidado',
      header: 'Monto Liquidado',
      pdfWidth: 28,  // Más ancha para mejor visualización
      excelWidth: 18,
      align: 'right',

      format: (item, ctx) => {
        const value = this.calcularMontoLiquidado(item);
        return this.formatMonto(value);
      },

      formatSubtotal: (item, ctx, allData) => {
        // NO usar el valor del backend porque está basado en subtotales.monto que es incorrecto
        // Recalcular sumando los items de la operatoria con estado válido
        const operatoriaNombre = (item._subtotales && item._subtotales.operatoria) || item.operatoria.replace('Subtotal ', '');

        // Filtrar solo items regulares (no subtotales) de la misma operatoria
        const itemsDeOperatoria = allData.filter(dataItem =>
          !dataItem._esSubtotal &&
          dataItem.operatoria === operatoriaNombre
        );

        // Sumar monto liquidado solo de items con estado válido
        const value = itemsDeOperatoria.reduce((total, dataItem) => {
          if (!this.esEstadoValidoParaMonto(dataItem)) {
            return total;
          }
          const montoLiquidado = this.calcularMontoLiquidado(dataItem);
          return total + montoLiquidado;
        }, 0);

        return this.formatMonto(value);
      },

      calculateTotal: (items, ctx) => {
        return items.reduce((total, item) => {
          const montoLiquidado = this.calcularMontoLiquidado(item);
          return total + montoLiquidado;
        }, 0);
      }
    });

    return columns;
  }

  /**
   * Determina si una columna debe incluirse según el tipo de reporte
   */
  shouldIncludeColumn(columnKey, options) {
    const reportType = options.reportType;

    // Info Mora e Info Cte y No Cte no incluyen Monto Aprobado ni Monto Pendiente
    if (reportType === 'Info Mora (Financiamiento)' || reportType === 'Info Cte y No Cte') {
      if (columnKey === 'monto_aprobado' || columnKey === 'monto_pendiente') {
        return false;
      }
    }

    return true;
  }

  /**
   * Obtiene el número máximo de desembolsos de los datos
   */
  getMaxDesembolsos(options) {
    // Obtener del contexto si está disponible
    if (options.maxDesembolsos !== undefined) {
      console.log(`✅ getMaxDesembolsos - Usando maxDesembolsos del contexto: ${options.maxDesembolsos}`);
      return options.maxDesembolsos;
    }

    // Por defecto, 7 desembolsos
    console.warn(`⚠️ getMaxDesembolsos - No se encontró maxDesembolsos en options, usando default: 7`);
    console.log('options:', options);
    return 7;
  }

  /**
   * Calcula el subtotal de un gasto para una operatoria
   */
  calcularSubtotalGasto(subtotalItem, campoGasto, getAllItems) {
    // Primero intentar usar el valor del backend en _subtotales.gastos
    if (subtotalItem._subtotales && subtotalItem._subtotales.gastos && subtotalItem._subtotales.gastos[campoGasto]) {
      return parseFloat(subtotalItem._subtotales.gastos[campoGasto]) || 0;
    }

    // Si no existe en el backend, retornar 0
    // (en el futuro podríamos calcular del frontend si tuviéramos acceso a todos los items)
    return 0;
  }

  /**
   * Calcula el monto liquidado
   */
  calcularMontoLiquidado(item) {
    // Solo calcular monto liquidado si el estado es "Otorgado" o "Cancelado"
    if (!this.esEstadoValidoParaMonto(item)) {
      return 0;
    }

    // Si hay desembolsos, calcular como suma de (monto + gastos) de cada desembolso
    if (item.desembolsos && Array.isArray(item.desembolsos) && item.desembolsos.length > 0) {
      return item.desembolsos.reduce((total, desembolso) => {
        const monto = parseFloat(desembolso.monto) || 0;
        const gastos = parseFloat(desembolso.gastos) || 0;
        return total + monto + gastos;
      }, 0);
    }

    // Si no hay desembolsos, usar el monto del parametro menos los gastos totales
    let monto = 0;
    if (item.parametros && item.parametros.monto && item.parametros.monto[0]) {
      monto = parseFloat(item.parametros.monto[0]) || 0;
    }

    let totalGastos = 0;
    if (item.gastos && item.gastos !== null && typeof item.gastos === 'object') {
      if (item.gastos.total) {
        totalGastos = parseFloat(item.gastos.total) || 0;
      }
    }

    return monto - totalGastos;
  }
}
